import { AppMenu } from './menu/AppMenu'
import { AudioPausedNotice } from './AudioPausedNotice'
import { ErrorNotice } from './ErrorNotice'
import { LevelMeter } from './LevelMeter'
import { Settings } from './settings/Settings'
import { TunerBars } from './tuner/TunerBars'
import { useTunerEngine } from './useTunerEngine'
import { useWakeLock } from './useWakeLock'
import { useTunerStore } from '../state/tuner-store'

const BUTTON_LABELS: Record<string, string> = {
  idle: 'Tuner starten',
  starting: 'Starte …',
  listening: 'Tuner stoppen',
  interrupted: 'Tuner stoppen',
  error: 'Erneut versuchen',
}

/** Application shell: transport, input selection, level, string meters. */
export function App(): React.JSX.Element {
  const status = useTunerStore((state) => state.status)
  const error = useTunerStore((state) => state.error)
  const tuning = useTunerStore((state) => state.tuning)
  const layout = useTunerStore((state) => state.layout)
  const notation = useTunerStore((state) => state.notation)
  const transposition = useTunerStore((state) => state.transposition)
  const timing = useTunerStore((state) => state.timing)
  const sampleRate = useTunerStore((state) => state.sampleRate)
  const { start, stop, selectDevice, refreshDevices, resume } = useTunerEngine()

  const isRunning = status === 'listening' || status === 'interrupted'
  const isBusy = status === 'starting'

  // Tuning is a two-handed job: nothing touches the screen for minutes at a
  // time, which is exactly when a phone locks itself.
  useWakeLock(status === 'listening')

  return (
    <main className="app">
      <header className="app__header">
        <div className="app__title">
          <h1>Multitimbral</h1>
          <AppMenu />
        </div>
        <p>
          Einmal über alle sechs Saiten streichen — jede wird einzeln und gleichzeitig verfolgt.
        </p>
      </header>

      <section className="app__transport">
        <button
          type="button"
          className="button"
          disabled={isBusy}
          onClick={isRunning ? stop : start}
        >
          {BUTTON_LABELS[status] ?? 'Tuner starten'}
        </button>
        <LevelMeter />
      </section>

      {status === 'interrupted' && <AudioPausedNotice onResume={resume} />}
      {error !== null && <ErrorNotice error={error} />}

      <TunerBars
        strings={tuning.strings}
        layout={layout}
        notation={notation}
        transposition={transposition}
      />

      <Settings onSelectDevice={selectDevice} onOpenDevices={refreshDevices} />

      {(sampleRate !== null || timing !== null) && (
        <p className="app__timing">
          {/* The rate the browser picked, said plainly: it is the first thing
              to compare when two devices read the same string differently. */}
          {sampleRate !== null && (
            <>
              Input: <b>{(sampleRate / 1000).toFixed(1)} kHz</b>.{' '}
            </>
          )}
          {timing !== null && (
            <>
              Audio thread: ~{timing.averageMs.toFixed(4)} ms per {timing.blockSize}-sample block —{' '}
              {timing.loadPercent.toFixed(3)} % of the {timing.budgetMs.toFixed(2)} ms budget.{' '}
              {timing.slowBlocks} of {timing.blocks} blocks reached the {timing.clockResolutionMs}{' '}
              ms clock tick.
            </>
          )}
        </p>
      )}
    </main>
  )
}
